import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { BehaviorSubject, lastValueFrom, map, Observable, of } from 'rxjs';
import { environment } from '../../../environments/environment';
import { AuthService } from './auth.service';
import { FirestoreService } from './firestore.service';
import { CountryCodeService } from './phone-code.service';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { Methodology, RawMethodologyResponse } from 'src/app/features/stock-details/types/screening-methodology.type';
import { isPlatformBrowser } from '@angular/common';

export interface WaitlistStatsResponse {
   status: boolean;
   message: string;
   data: WaitlistStats;
}

export interface WaitlistStats {
   total: number;
}

export interface MusaffaInvestments {
   status: boolean;
   message: string;
   data: InvestmentData;
}

export interface InvestmentData {
   total_amount_invested: number;
   times_invested: number;
   base_shares_purchased: number;
   bonus_shares_received: number;
   total_shares_owned: number;
   price_paid_per_share: number;
   effective_price_per_share: number;
   latest_share_price: string;
   estimated_value: number;
   currency: string;
   links: any;
   investments: Investment[];
}

export interface Investment {
   round: string;
   amount_invested: number;
   price_per_share: string;
   shares_purchased: number;
   bonus_breakdown: BonusBreakdown[];
   total_bonus: number;
   total_shares: number;
   certificate_url: string;
   currency: string;
   effective_price_per_share: number;
   estimated_value: number;
}

export interface BonusBreakdown {
   label: string;
   key: string;
   value: number;
}

@Injectable({
   providedIn: 'root',
})
export class ApiService {
   private newApi = environment.apiUrl;
   private api = environment.oldServerApiUrl;
   private purificationApi = environment.purificationApiUrl;
   private relatedStocksCache = new Map<string, any>();
   private authUrl: string = environment.authUrl;

   constructor(
      private http: HttpClient,
      @Inject(PLATFORM_ID) private platformId: Object
   ) {
      const isBrowser = isPlatformBrowser(this.platformId);
      if(isBrowser) {
         this.token = localStorage?.getItem('token'); // Make sure key is correct
      }
   }

   public selectedCurrency = '';
   token: string|null = null;

   setSelectedCurrency(currency: string) {
      this.selectedCurrency = currency;
   }

   // getSelectedCurrency() {
   //    return this.selectedCurrency.asObservable();
   // }

   //charts-api
   // public stockCandles(body: any): any {
   //    return this.http.post<any>(this.api + '/api/v1/stocks/candles', body);
   // }

   public stockCandles(body: any): any {
      return this.http.post<any>(this.newApi + '/api/stock/candles', body);
   }
   //etf charts
   public etfCandles(body: any): any {
      return this.http.post<any>(this.api + '/api/v1/etf/candles', body);
   }

   // Stripe payment

   public stripePay(data: any): any {
      return this.http.post<any>(
         this.api + '/api/v1/stripe/do    -subscription',
         data
      );
   }

   public logout(token: string): void {
      const headers = new HttpHeaders({
         Authorization: `Bearer ${token}`,
         'Content-Type': 'application/json',
      });
      this.http.post(`${this.authUrl}/api/user/logout`, {}, { headers })
   }

   //stripe-api

   public stripeMembership(data: any): any {
      return this.http.post<any>(
         this.api + '/api/v1/stripe/do-subscription',
         data
      );
   }
   // refresh token

   public refreshToken(data: any): any {
      return this.http.post<any>(this.api + '/api/v1/auth/refreshToken', data);
   }

   //Analitcs request
   public requestAnalitcs(stockName: any, companyName: any): any {
      return this.http.post<any>(this.api + '/api/v1/notification/task', {
         stockName: stockName,
         companyName: companyName,
      });
   }

   //Etf-market-cap-breakdown
   public etfMarketCap(symbol: string) {
      return this.http.get<any>(
         this.api + '/api/v1/etf/market-cap-breakdown/' + symbol
      );
   }

   public async purificationLimitCheck() {
      const timezoneOffset = -1 * new Date().getTimezoneOffset();
      let params = {
         timeZoneOffset: timezoneOffset,
      };
      return await lastValueFrom(
         this.http.post<any>(
            this.newApi + '/api/purification/check-limit',
            params
         )
      );
   }

   public async purificationCalculation(
      symbol: any,
      count: any,
      start: any,
      end: any
   ): Promise<any> {
      // const endPoint = `${this.merlinApi}/etfcompliance/purification/${symbol}/${count}/${start}/${end}`;
      // const data = await lastValueFrom(
      //   this.http.get<any>(endPoint)
      // );
      // return data;

      const timezoneOffset = -1 * new Date().getTimezoneOffset();

      let params = {
         Ticker: symbol,
         NumberOfShares: count,
         BuyDate: start,
         SellDate: end,
         timeZoneOffset: timezoneOffset,
      };
      return await lastValueFrom(
         this.http.post<any>(this.newApi + '/api/purification/v2', params, {
            // headers: new HttpHeaders({
            //   'Authorization': 'Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJyb2xlIjoiVVNFUixQUkVNSVVNIiwiaWQiOiI0MTE3NzM3Yy0zMDQ0LTRmZjctODljNi1jN2I4YmUwMWVkZTgiLCJzdWIiOiJhYmR1cmFzaGlkLmxAbXVzYWZmYS5jb20iLCJqdGkiOiI0MTE3NzM3Yy0zMDQ0LTRmZjctODljNi1jN2I4YmUwMWVkZTgiLCJpYXQiOjE2Njg2NTg3MDIsImV4cCI6MTY2ODc0NTEzMn0.bHmJJkzc3XAnctSrgTRnogXPYK8f5N3w0VoHoOD4AGE',
            // })
         })
      );
   }

   public async purificationSampleCalculation(): Promise<any> {
      return await lastValueFrom(
         this.http.get<any>(this.newApi + '/api/purification/example')
      );
   }

   public async updateComplianceChangeRequest(ticker: string): Promise<any> {
      return await lastValueFrom(
         this.http.post<any>(
            this.newApi + '/api/user/compliance-update/store',
            { ticker }
         )
      );
   }

   public getUserCountry(): any {
      return this.http.get<any>(
         'https://api.ipregistry.co/?key=r4k1ipy7id5o6u8u&fields=location.country.name,location.country.code'
      );
   }

   async getRelatedStocks(
      ticker: string,
      country: string = '',
      gsubind: string = ''
   ): Promise<any> {
      const cacheKey = `ticker`;

      if (this.relatedStocksCache.has(cacheKey)) {
         return this.relatedStocksCache.get(cacheKey);
      }

      const response = await lastValueFrom(
         this.http.post<any>(this.newApi + '/api/get-related-stocks', {
            ticker,
            country,
            gsubind,
         })
      );

      this.relatedStocksCache.set(cacheKey, response);
      return response;
   }

   async getUserCountryCode() {
      return await lastValueFrom(
         this.http.get<any>(this.newApi + '/api/country')
      );
   }

   async getAreSymbolsCovered(symbols: string[], type: string) {
      return await lastValueFrom(
         this.http.post<any>(this.newApi + `/api/user/are-symbols-covered`, {
            symbols,
            type,
         })
      );
   }

   async getStockComplianceMeta(ticker: string) {
      return await lastValueFrom(
         this.http.post<any>(this.newApi + `/api/user/compliance-report-meta`, {
            ticker,
            type: 'stock',
         })
      );
   }

   async getEtfComplianceMeta(ticker: string) {
      return await lastValueFrom(
         this.http.post<any>(this.newApi + `/api/user/compliance-report-meta`, {
            ticker,
            type: 'etf',
         })
      );
   }

   async getSampleStockComplianceInfo() {
      return await lastValueFrom(
         this.http.get<any>(
            this.newApi +
               `/api/user/get-detailed-compliance-sample-report/stock`
         )
      );
   }

   async getSampleEtfComplianceInfo() {
      return await lastValueFrom(
         this.http.get<any>(
            this.newApi + `/api/user/get-detailed-compliance-sample-report/etf`
         )
      );
   }

   async getStockComplianceInfo(ticker: string) {
      return await lastValueFrom(
         this.http.post<any>(
            this.newApi + `/api/user/get-stock-detailed-compliance-report`,
            { ticker }
         )
      );
   }

   async getEtfComplianceInfo(ticker: string) {
      return await lastValueFrom(
         this.http.post<any>(
            this.newApi + `/api/user/get-etf-detailed-compliance-report`,
            { ticker }
         )
      );
   }

   async getMusaffaInvestments() {
      return await lastValueFrom(
         this.http.get<MusaffaInvestments>(
            this.newApi + `/api/musaffa/investments`
         )
      );
   }

   async getMusaffaInvestmentSample() {
      return await lastValueFrom(
         this.http.get<any>(this.newApi + `/api/musaffa/investments/sample`)
      );
   }

   async sendOTP(email: string) {
      return await lastValueFrom(
         this.http.post(`${this.newApi}/api/musaffa/send-otp/email`, {
            email,
         })
      );
   }

   async verifyOTP(email: string, otp: string) {
      return await lastValueFrom(
         this.http.post(`${this.newApi}/api/musaffa/verify-otp/email`, {
            email,
            otp,
         })
      );
   }

   async onboardingPost(data: any) {
      return await lastValueFrom(
         this.http.post(`${this.newApi}/api/user/onboarding`, data)
      );
   }

   async setOnboardingStatus(status: 'started' | 'skipped' | 'completed') {
      return await lastValueFrom(
         this.http.post(`${this.newApi}/api/user/onboarding/status`, { status })
      );
   }

   async getOnboardingInfo() {
      return await lastValueFrom(
         this.http.get(`${this.newApi}/api/user/onboarding`)
      );
   }

   async claimPremiumAccess() {
      return await lastValueFrom(
         this.http.post(`${this.newApi}/api/claim-premium-access`, {})
      );
   }

async sendMobileOtp(mobile: string): Promise<any> {
      const headers = new HttpHeaders({
         Authorization: `Bearer ${this.token}`,
         'Content-Type': 'application/json',
      });

      const payload = { mobile };

      return await lastValueFrom(
         this.http.post(`${this.newApi}/api/otp/mobile/send`, payload, { headers })
      );
   }

   async verifyMobileOtp(mobile: string, otp: string): Promise<any> {
      const headers = new HttpHeaders({
         'Content-Type': 'application/json',
      });

      const payload = {
         mobile,
         otp,
         is_update: 'true', // Match cURL payload
      };

      return await lastValueFrom(
         this.http.post(`${this.newApi}/api/otp/mobile/verify`, payload, { headers })
      );
   }

   async submitWaitlistEmail(email: string, referralCode: string | null): Promise<any> {
      return await lastValueFrom(
         this.http.post(`${this.newApi}/api/trading-waitlist`, { email, referral_code: referralCode })
      );
   }
   
   async getWaitlistStats(): Promise<WaitlistStatsResponse> {
      return await lastValueFrom(
         this.http.get<WaitlistStatsResponse>(`${this.newApi}/api/trading-waitlist/stats`)
      );
   }

   async getEmailWaitlist(email: string): Promise<any> {
      return await lastValueFrom(
         this.http.post<any>(`${this.newApi}/api/trading-waitlist/info`, { email })
      );
   }

   async makeVipWaitlistUser(): Promise<any> {
      return await lastValueFrom(
         this.http.get<any>(`${this.newApi}/api/trading-waitlist/mark-vip`)
      );
   }

   getStockDetailedComplianceReport(ticker: string): Observable<any>{
      return this.http.post<any>(this.newApi + `/api/user/get-stock-detailed-compliance-report`, { ticker })
   }

   getStockDetailedComplianceReportMockData(): Observable<any> {

      const mockData = {
         "aaoifi_status": "COMPLIANT",
         "accountsReceivable": 46835,
         "cbaComment": "",
         "cbaStatus": "Pass",
         "companyName": "Apple Inc",
         "country": "US",
         "currency": "USD",
         "debt": 3.52,
         "debtRevenue": 102677400000,
         "debtStatus": "Pass",
         "djim_status": "UNCOVERED",
         "exchange": "NASDAQ",
         "ftse_status": "UNCOVERED",
         "halal": 95.4641,
         "halalRevenue": 302210300000,
         "id": "AAPL",
         "identifier": "2decccff-face-4a48-8a99-f2c28ee69a40",
         "impermissibleInterestIncomeAmountInOnce": 0,
         "interestBearingDebtJson.longTermJson.amount": 83294.5,
         "interestBearingDebtJson.longTermJson.amountInOnes": 83294500000,
         "interestBearingDebtJson.longTermJson.islamicAmount": 0,
         "interestBearingDebtJson.longTermJson.islamicAmountInOnes": 0,
         "interestBearingDebtJson.longTermJson.islamicPercentage": 0,
         "interestBearingDebtJson.longTermJson.items": [
            {
                  "amount": "82430",
                  "amountInOnes": 82430000000,
                  "comment": "",
                  "id": "1",
                  "items": [],
                  "itemsNotHalal": 0,
                  "itemsQuestionable": 0,
                  "name": "Term debt",
                  "percentage": 2.82383434128237,
                  "selector": ""
            },
            {
                  "amount": "864.5",
                  "amountInOnes": 864500000,
                  "comment": "",
                  "id": "2",
                  "items": [],
                  "itemsNotHalal": 0,
                  "itemsQuestionable": 0,
                  "name": "Finance leases (deduced)",
                  "percentage": 0.029615489361138045,
                  "selector": ""
            }
         ],
         "interestBearingDebtJson.longTermJson.name": "Long-term",
         "interestBearingDebtJson.longTermJson.nonIslamicAmount": 83294.5,
         "interestBearingDebtJson.longTermJson.nonIslamicAmountInOnes": 83294500000,
         "interestBearingDebtJson.longTermJson.nonIslamicPercentage": 0,
         "interestBearingDebtJson.longTermJson.retio": 2.8534498306435085,
         "interestBearingDebtJson.shortTermJson.amount": 19382.9,
         "interestBearingDebtJson.shortTermJson.amountInOnes": 19382900000,
         "interestBearingDebtJson.shortTermJson.islamicAmount": 0,
         "interestBearingDebtJson.shortTermJson.islamicAmountInOnes": 0,
         "interestBearingDebtJson.shortTermJson.islamicPercentage": 0,
         "interestBearingDebtJson.shortTermJson.items": [
            {
                  "amount": "9923",
                  "amountInOnes": 9923000000,
                  "comment": [
                     {
                        "body": "",
                        "files": []
                     }
                  ],
                  "id": "1",
                  "items": [],
                  "itemsNotHalal": 0,
                  "itemsQuestionable": 0,
                  "name": "Commercial paper",
                  "percentage": 0.33993580211749314,
                  "selector": ""
            },
            {
                  "amount": "9345",
                  "amountInOnes": 9345000000,
                  "comment": [
                     {
                        "body": "",
                        "files": []
                     }
                  ],
                  "id": "2",
                  "items": [],
                  "itemsNotHalal": 0,
                  "itemsQuestionable": 0,
                  "name": "Term debt",
                  "percentage": 0.3201350469402372,
                  "selector": ""
            },
            {
                  "amount": "114.9",
                  "amountInOnes": 114900000,
                  "comment": [
                     {
                        "body": "",
                        "files": []
                     }
                  ],
                  "id": "3",
                  "items": [],
                  "itemsNotHalal": 0,
                  "itemsQuestionable": 0,
                  "name": "Finance leases (deduced)",
                  "percentage": 0.003936170882122339,
                  "selector": ""
            }
         ],
         "interestBearingDebtJson.shortTermJson.name": "Short-term",
         "interestBearingDebtJson.shortTermJson.nonIslamicAmount": 19382.9,
         "interestBearingDebtJson.shortTermJson.nonIslamicAmountInOnes": 19382900000,
         "interestBearingDebtJson.shortTermJson.nonIslamicPercentage": 0,
         "interestBearingDebtJson.shortTermJson.retio": 0.6640070199398527,
         "interestBearingDebtJson.totalAmount": 102677.4,
         "interestBearingDebtJson.totalNonIslamicAmount": 102677.4,
         "interestBearingDebtJson.totalRetio": 3.5174568505833608,
         "interestIncomeJson.amount": 2874.49,
         "interestIncomeJson.amountInOnes": 2874490000,
         "interestIncomeJson.halalAmount": 0,
         "interestIncomeJson.halalAmountInOnes": 0,
         "interestIncomeJson.halalPercentage": 0,
         "interestIncomeJson.items": [
            {
                  "amount": 415,
                  "amountInOnes": 415000000,
                  "comment": "",
                  "id": 1,
                  "items": [
                     {
                        "amount": "2459.49",
                        "amountInOnes": 2459490000,
                        "comment": [
                              {
                                 "body": "Apple doesn't separately disclose interest income, as it's included under \u201cOther income, net.\u201d We estimated it using the 2023 Annual Report.",
                                 "files": []
                              }
                        ],
                        "id": "1",
                        "items": [],
                        "itemsNotHalal": 0,
                        "itemsQuestionable": 0,
                        "name": "Interest income (deduced)",
                        "percentage": 0.7769194687712955,
                        "selector": "NON_COMPLIANT"
                     },
                     {
                        "amount": "415",
                        "amountInOnes": 415000000,
                        "comment": "",
                        "id": "2",
                        "items": [],
                        "itemsNotHalal": 0,
                        "itemsQuestionable": 0,
                        "name": "Gain on derivative instruments",
                        "percentage": 0.13109286052803132,
                        "selector": "NON_COMPLIANT"
                     }
                  ],
                  "itemsNotHalal": 2874.49,
                  "itemsQuestionable": 0,
                  "name": "Interest Income",
                  "percentage": 0.13109286052803132,
                  "showCalc": true
            }
         ],
         "interestIncomeJson.name": "OTHER INCOME",
         "interestIncomeJson.notHalalAmount": 2874.49,
         "interestIncomeJson.notHalalAmountInOnes": 2874490000,
         "interestIncomeJson.notHalalPercentage": 0.9080123292993267,
         "interestIncomeJson.percentage": 0.13109286052803132,
         "interestIncomeJson.questionableAmount": 0,
         "interestIncomeJson.questionableAmountInOnes": 0,
         "interestIncomeJson.questionablePercentage": 0,
         "is_ipo": "0",
         "isin": "US0378331005",
         "issi_status": "UNCOVERED",
         "lastUpdate": 1754376145,
         "mainCurrency": "USD",
         "marketCapitalization": "2986418.4157824",
         "msci_status": "UNCOVERED",
         "mscim_status": "UNCOVERED",
         "notHalal": 4.54,
         "notHalalRevenue": 14359190000,
         "questionable": 0,
         "questionableRevenue": 0,
         "ranking": 1,
         "ranking_v2": 2,
         "reportDate": "2025-06-28",
         "reportFormat": "NEW",
         "reportSource": "https:\/\/www.sec.gov\/Archives\/edgar\/data\/320193\/000032019325000073\/aapl-20250628.htm#i47143780af9f4d3d85123ce19d9f33bd_13",
         "reportTypeSection": "THIRD_QUARTER",
         "reportTypeYear": "2025",
         "revenueBreakdownJson.amount": 313695,
         "revenueBreakdownJson.amountInOnes": 313695000000,
         "revenueBreakdownJson.halalAmount": 302210.3,
         "revenueBreakdownJson.halalAmountInOnes": 302210300000,
         "revenueBreakdownJson.halalPercentage": 95.46412700731203,
         "revenueBreakdownJson.items": [
            {
                  "amount": "160561",
                  "amountInOnes": 160561000000,
                  "comment": "",
                  "id": "1",
                  "items": [
                     {
                        "amount": "160561",
                        "amountInOnes": 160561000000,
                        "comment": "",
                        "id": "1",
                        "items": [],
                        "itemsNotHalal": 0,
                        "itemsQuestionable": 0,
                        "name": "iPhone",
                        "openComment": false,
                        "percentage": 50.71903802226804,
                        "selector": "COMPLIANT"
                     }
                  ],
                  "itemsNotHalal": 0,
                  "itemsQuestionable": 0,
                  "name": "iPhone",
                  "openComment": false,
                  "percentage": 50.71903802226804,
                  "selector": "COMPLIANT"
            },
            {
                  "amount": "24982",
                  "amountInOnes": 24982000000,
                  "comment": "",
                  "id": "2",
                  "items": [
                     {
                        "amount": "24982",
                        "amountInOnes": 24982000000,
                        "comment": "",
                        "id": "2",
                        "items": [],
                        "itemsNotHalal": 0,
                        "itemsQuestionable": 0,
                        "name": "Mac",
                        "openComment": false,
                        "percentage": 7.891474317376574,
                        "selector": "COMPLIANT"
                     }
                  ],
                  "itemsNotHalal": 0,
                  "itemsQuestionable": 0,
                  "name": "Mac",
                  "openComment": false,
                  "percentage": 7.891474317376574,
                  "selector": "COMPLIANT"
            },
            {
                  "amount": "21071",
                  "amountInOnes": 21071000000,
                  "comment": "",
                  "id": "3",
                  "items": [
                     {
                        "amount": "21071",
                        "amountInOnes": 21071000000,
                        "comment": "",
                        "id": "3",
                        "items": [],
                        "itemsNotHalal": 0,
                        "itemsQuestionable": 0,
                        "name": "iPad",
                        "openComment": false,
                        "percentage": 6.656042564303969,
                        "selector": "COMPLIANT"
                     }
                  ],
                  "itemsNotHalal": 0,
                  "itemsQuestionable": 0,
                  "name": "iPad",
                  "openComment": false,
                  "percentage": 6.656042564303969,
                  "selector": "COMPLIANT"
            },
            {
                  "amount": "26673",
                  "amountInOnes": 26673000000,
                  "comment": "",
                  "id": "4",
                  "items": [
                     {
                        "amount": "26673",
                        "amountInOnes": 26673000000,
                        "comment": "",
                        "id": "4",
                        "items": [],
                        "itemsNotHalal": 0,
                        "itemsQuestionable": 0,
                        "name": "Wearables, Home and Accessories",
                        "openComment": false,
                        "percentage": 8.425638238226938,
                        "selector": "COMPLIANT"
                     }
                  ],
                  "itemsNotHalal": 0,
                  "itemsQuestionable": 0,
                  "name": "Wearables, Home and Accessories",
                  "openComment": false,
                  "percentage": 8.425638238226938,
                  "selector": "COMPLIANT"
            },
            {
                  "amount": "26504.7",
                  "amountInOnes": 26504700000,
                  "comment": [
                     {
                        "body": "Since Apple does not provide a detailed breakdown of revenue by individual service, we relied on Trefis.com\u2019s estimates for fiscal year 2024 to approximate the revenue generated by the App Store for Shariah compliance analysis. The App Store is Apple\u2019s digital platform for distributing mobile applications on iOS and iPadOS devices, offering a broad selection of apps such as games, productivity software, and multimedia tools.",
                        "files": []
                     }
                  ],
                  "id": "5",
                  "items": [
                     {
                        "amount": "26504.7",
                        "amountInOnes": 26504700000,
                        "comment": [
                              {
                                 "body": "Since Apple does not provide a detailed breakdown of revenue by individual service, we relied on Trefis.com\u2019s estimates for fiscal year 2024 to approximate the revenue generated by the App Store for Shariah compliance analysis. The App Store is Apple\u2019s digital platform for distributing mobile applications on iOS and iPadOS devices, offering a broad selection of apps such as games, productivity software, and multimedia tools.",
                                 "files": []
                              }
                        ],
                        "id": "5",
                        "items": [],
                        "itemsNotHalal": 0,
                        "itemsQuestionable": 0,
                        "name": "AppStore Revenue",
                        "openComment": false,
                        "percentage": 8.37247455527063,
                        "selector": "COMPLIANT"
                     }
                  ],
                  "itemsNotHalal": 0,
                  "itemsQuestionable": 0,
                  "name": "AppStore Revenue",
                  "openComment": false,
                  "percentage": 8.37247455527063,
                  "selector": "COMPLIANT"
            },
            {
                  "amount": "14548.3",
                  "amountInOnes": 14548300000,
                  "comment": [
                     {
                        "body": "Since Apple does not provide a detailed breakdown of revenue by individual service, we relied on Trefis.com\u2019s estimates for fiscal year 2024 to approximate the revenue attributed to the licensing segment. Apple's licensing segment generates revenue primarily through agreements with third parties. One of the most significant sources of licensing revenue comes from Google, which pays Apple to be the default search engine on Apple devices. These payments are known as traffic acquisition costs (TAC). In addition to Google, Apple receives licensing payments from other third parties for various services and technologies.",
                        "files": []
                     }
                  ],
                  "id": "6",
                  "items": [
                     {
                        "amount": "14548.3",
                        "amountInOnes": 14548300000,
                        "comment": [
                              {
                                 "body": "Since Apple does not provide a detailed breakdown of revenue by individual service, we relied on Trefis.com\u2019s estimates for fiscal year 2024 to approximate the revenue attributed to the licensing segment. Apple's licensing segment generates revenue primarily through agreements with third parties. One of the most significant sources of licensing revenue comes from Google, which pays Apple to be the default search engine on Apple devices. These payments are known as traffic acquisition costs (TAC). In addition to Google, Apple receives licensing payments from other third parties for various services and technologies.",
                                 "files": []
                              }
                        ],
                        "id": "6",
                        "items": [],
                        "itemsNotHalal": 0,
                        "itemsQuestionable": 0,
                        "name": "Licensing Revenue",
                        "openComment": false,
                        "percentage": 4.59561027185532,
                        "selector": "COMPLIANT"
                     }
                  ],
                  "itemsNotHalal": 0,
                  "itemsQuestionable": 0,
                  "name": "Licensing Revenue",
                  "openComment": false,
                  "percentage": 4.59561027185532,
                  "selector": "COMPLIANT"
            },
            {
                  "amount": "7106.9",
                  "amountInOnes": 7106900000,
                  "comment": [
                     {
                        "body": "Since Apple does not provide a detailed breakdown of revenue by individual service, we relied on warrantyweek.com\u2019s estimates to approximate the revenue generated by the Apple Care. Apple devices come with a one-year limited warranty and up to 90 days of complimentary technical support. To extend coverage, Apple offers AppleCare+, which provides additional protection, including accidental damage coverage and extended support.",
                        "files": []
                     }
                  ],
                  "id": "7",
                  "items": [
                     {
                        "amount": "7106.9",
                        "amountInOnes": 7106900000,
                        "comment": [
                              {
                                 "body": "Since Apple does not provide a detailed breakdown of revenue by individual service, we relied on warrantyweek.com\u2019s estimates to approximate the revenue generated by the Apple Care. Apple devices come with a one-year limited warranty and up to 90 days of complimentary technical support. To extend coverage, Apple offers AppleCare+, which provides additional protection, including accidental damage coverage and extended support.",
                                 "files": []
                              }
                        ],
                        "id": "7",
                        "items": [],
                        "itemsNotHalal": 0,
                        "itemsQuestionable": 0,
                        "name": "Apple Care",
                        "openComment": false,
                        "percentage": 2.244973133702809,
                        "selector": "COMPLIANT"
                     }
                  ],
                  "itemsNotHalal": 0,
                  "itemsQuestionable": 0,
                  "name": "Apple Care",
                  "openComment": false,
                  "percentage": 2.244973133702809,
                  "selector": "COMPLIANT"
            },
            {
                  "amount": "2118.1",
                  "amountInOnes": 2118100000,
                  "comment": [
                     {
                        "body": "Since Apple does not provide a detailed breakdown of revenue by individual service, we relied on WSJ\u2019s estimates to approximate the revenue generated by the Apple Pay. Apple Pay is a mobile payment and digital wallet service by Apple Inc., available on iPhone, iPad, and Apple Watch. Apple Pay generates revenue through transaction fees charged to merchants.",
                        "files": []
                     }
                  ],
                  "id": "8",
                  "items": [
                     {
                        "amount": "2118.1",
                        "amountInOnes": 2118100000,
                        "comment": [
                              {
                                 "body": "Since Apple does not provide a detailed breakdown of revenue by individual service, we relied on WSJ\u2019s estimates to approximate the revenue generated by the Apple Pay. Apple Pay is a mobile payment and digital wallet service by Apple Inc., available on iPhone, iPad, and Apple Watch. Apple Pay generates revenue through transaction fees charged to merchants.",
                                 "files": []
                              }
                        ],
                        "id": "8",
                        "items": [],
                        "itemsNotHalal": 0,
                        "itemsQuestionable": 0,
                        "name": "Apple Pay",
                        "openComment": false,
                        "percentage": 0.6690790069504171,
                        "selector": "COMPLIANT"
                     }
                  ],
                  "itemsNotHalal": 0,
                  "itemsQuestionable": 0,
                  "name": "Apple Pay",
                  "openComment": false,
                  "percentage": 0.6690790069504171,
                  "selector": "COMPLIANT"
            },
            {
                  "amount": "1226.3",
                  "amountInOnes": 1226300000,
                  "comment": [
                     {
                        "body": "Since Apple does not provide a detailed breakdown of revenue by individual service, we relied on Forbes\u2019s estimates to approximate the revenue generated by the Apple Card. Apple Card is a credit card developed by Apple in partnership with Goldman Sachs, generating revenue primarily through interest charges and interchange fees, with no late or annual fees.",
                        "files": []
                     }
                  ],
                  "id": "9",
                  "items": [
                     {
                        "amount": "1226.3",
                        "amountInOnes": 1226300000,
                        "comment": [
                              {
                                 "body": "Since Apple does not provide a detailed breakdown of revenue by individual service, we relied on Forbes\u2019s estimates to approximate the revenue generated by the Apple Card. Apple Card is a credit card developed by Apple in partnership with Goldman Sachs, generating revenue primarily through interest charges and interchange fees, with no late or annual fees.",
                                 "files": []
                              }
                        ],
                        "id": "9",
                        "items": [],
                        "itemsNotHalal": 0,
                        "itemsQuestionable": 0,
                        "name": "Apple Card",
                        "openComment": false,
                        "percentage": 0.38737150570005974,
                        "selector": "NON_COMPLIANT"
                     }
                  ],
                  "itemsNotHalal": 0,
                  "itemsQuestionable": 0,
                  "name": "Apple Card",
                  "openComment": false,
                  "percentage": 0.38737150570005974,
                  "selector": "NON_COMPLIANT"
            },
            {
                  "amount": "7441.4",
                  "amountInOnes": 7441400000,
                  "comment": [
                     {
                        "body": "Since Apple does not provide a detailed breakdown of revenue by individual service, we relied on Trefis.com\u2019s estimates for fiscal year 2024 to approximate the revenue generated by the Apple Music for Shariah compliance analysis. Apple Music is a streaming service developed by Apple Inc., offering access to over 100 million songs, curated playlists, live radio stations, and exclusive content.",
                        "files": []
                     }
                  ],
                  "id": "10",
                  "items": [
                     {
                        "amount": "7441.4",
                        "amountInOnes": 7441400000,
                        "comment": [
                              {
                                 "body": "Since Apple does not provide a detailed breakdown of revenue by individual service, we relied on Trefis.com\u2019s estimates for fiscal year 2024 to approximate the revenue generated by the Apple Music for Shariah compliance analysis. Apple Music is a streaming service developed by Apple Inc., offering access to over 100 million songs, curated playlists, live radio stations, and exclusive content.",
                                 "files": []
                              }
                        ],
                        "id": "10",
                        "items": [],
                        "itemsNotHalal": 0,
                        "itemsQuestionable": 0,
                        "name": "Apple Music Revenue",
                        "openComment": false,
                        "percentage": 2.3506371381525115,
                        "selector": "NON_COMPLIANT"
                     }
                  ],
                  "itemsNotHalal": 0,
                  "itemsQuestionable": 0,
                  "name": "Apple Music Revenue",
                  "openComment": false,
                  "percentage": 2.3506371381525115,
                  "selector": "NON_COMPLIANT"
            },
            {
                  "amount": "8695.6",
                  "amountInOnes": 8695600000,
                  "comment": [
                     {
                        "body": "Since Apple does not provide a detailed breakdown of revenue by individual service, we relied on Trefis.com\u2019s estimates for fiscal year 2024 to approximate the revenue generated by the Apple iCloud for Shariah compliance analysis. Apple iCloud is a cloud storage and computing service by Apple Inc. It allows users to store photos, videos, documents, and more, syncing them across all Apple devices.",
                        "files": []
                     }
                  ],
                  "id": "11",
                  "items": [
                     {
                        "amount": "8695.6",
                        "amountInOnes": 8695600000,
                        "comment": [
                              {
                                 "body": "Since Apple does not provide a detailed breakdown of revenue by individual service, we relied on Trefis.com\u2019s estimates for fiscal year 2024 to approximate the revenue generated by the Apple iCloud for Shariah compliance analysis. Apple iCloud is a cloud storage and computing service by Apple Inc. It allows users to store photos, videos, documents, and more, syncing them across all Apple devices.",
                                 "files": []
                              }
                        ],
                        "id": "11",
                        "items": [],
                        "itemsNotHalal": 0,
                        "itemsQuestionable": 0,
                        "name": "iCloud Revenue",
                        "openComment": false,
                        "percentage": 2.7468218747169857,
                        "selector": "COMPLIANT"
                     }
                  ],
                  "itemsNotHalal": 0,
                  "itemsQuestionable": 0,
                  "name": "iCloud Revenue",
                  "openComment": false,
                  "percentage": 2.7468218747169857,
                  "selector": "COMPLIANT"
            },
            {
                  "amount": "9949.7",
                  "amountInOnes": 9949700000,
                  "comment": [
                     {
                        "body": "Since Apple does not provide a detailed breakdown of revenue by individual service, we relied on Trefis.com\u2019s estimates for fiscal year 2024 to approximate the revenue attributed to the third-party subscription segment. The third-party subscription on Apple is a recurring payment for a service or app not provided by Apple itself. It can be managed either through Apple's App Store payment system or directly by the service provider.",
                        "files": []
                     }
                  ],
                  "id": "12",
                  "items": [
                     {
                        "amount": "9949.7",
                        "amountInOnes": 9949700000,
                        "comment": [
                              {
                                 "body": "Since Apple does not provide a detailed breakdown of revenue by individual service, we relied on Trefis.com\u2019s estimates for fiscal year 2024 to approximate the revenue attributed to the third-party subscription segment. The third-party subscription on Apple is a recurring payment for a service or app not provided by Apple itself. It can be managed either through Apple's App Store payment system or directly by the service provider.",
                                 "files": []
                              }
                        ],
                        "id": "12",
                        "items": [],
                        "itemsNotHalal": 0,
                        "itemsQuestionable": 0,
                        "name": "Third-Party Subscription Revenue",
                        "openComment": false,
                        "percentage": 3.14297502264037,
                        "selector": "COMPLIANT"
                     }
                  ],
                  "itemsNotHalal": 0,
                  "itemsQuestionable": 0,
                  "name": "Third-Party Subscription Revenue",
                  "openComment": false,
                  "percentage": 3.14297502264037,
                  "selector": "COMPLIANT"
            },
            {
                  "amount": "2817",
                  "amountInOnes": 2817000000,
                  "comment": [
                     {
                        "body": "Since Apple does not provide a detailed breakdown of revenue by individual service, we relied on Trefis.com\u2019s estimates for fiscal year 2024 to approximate the revenue generated by the App TV+ for Shariah compliance analysis. Apple TV+ is Apple's streaming service, offering a range of original shows, movies, and documentaries known as \"Apple Originals.\"",
                        "files": []
                     }
                  ],
                  "id": "13",
                  "items": [
                     {
                        "amount": "2817",
                        "amountInOnes": 2817000000,
                        "comment": [
                              {
                                 "body": "Since Apple does not provide a detailed breakdown of revenue by individual service, we relied on Trefis.com\u2019s estimates for fiscal year 2024 to approximate the revenue generated by the App TV+ for Shariah compliance analysis. Apple TV+ is Apple's streaming service, offering a range of original shows, movies, and documentaries known as \"Apple Originals.\"",
                                 "files": []
                              }
                        ],
                        "id": "13",
                        "items": [],
                        "itemsNotHalal": 0,
                        "itemsQuestionable": 0,
                        "name": "Apple TV+ Revenue",
                        "openComment": false,
                        "percentage": 0.8898520195360584,
                        "selector": "NON_COMPLIANT"
                     }
                  ],
                  "itemsNotHalal": 0,
                  "itemsQuestionable": 0,
                  "name": "Apple TV+ Revenue",
                  "openComment": false,
                  "percentage": 0.8898520195360584,
                  "selector": "NON_COMPLIANT"
            }
         ],
         "revenueBreakdownJson.name": "SALES BREAKDOWN",
         "revenueBreakdownJson.notHalalAmount": 11484.7,
         "revenueBreakdownJson.notHalalAmountInOnes": 11484699999.999998,
         "revenueBreakdownJson.notHalalPercentage": 3.6278606633886286,
         "revenueBreakdownJson.percentage": 99.09198767070068,
         "revenueBreakdownJson.questionableAmount": 0,
         "revenueBreakdownJson.questionableAmountInOnes": 0,
         "revenueBreakdownJson.questionablePercentage": 0,
         "revenueBreakdownStatus": "Pass",
         "sac_status": "UNCOVERED",
         "securitiesAndAssets": 4.56,
         "securitiesAndAssetsJson.longTermJson.amount": 77614,
         "securitiesAndAssetsJson.longTermJson.amountInOnes": 77614000000,
         "securitiesAndAssetsJson.longTermJson.islamicAmount": 0,
         "securitiesAndAssetsJson.longTermJson.islamicAmountInOnes": 0,
         "securitiesAndAssetsJson.longTermJson.islamicPercentage": 0,
         "securitiesAndAssetsJson.longTermJson.items": [
            {
                  "amount": "77614",
                  "amountInOnes": 77614000000,
                  "comment": [
                     {
                        "body": "",
                        "files": []
                     }
                  ],
                  "id": "1",
                  "items": [],
                  "itemsNotHalal": 0,
                  "itemsQuestionable": 0,
                  "name": "Marketable securities",
                  "percentage": 2.6588508863798364,
                  "selector": "NON_ISLAMIC"
            }
         ],
         "securitiesAndAssetsJson.longTermJson.name": "Long-term",
         "securitiesAndAssetsJson.longTermJson.nonIslamicAmount": 77614,
         "securitiesAndAssetsJson.longTermJson.nonIslamicAmountInOnes": 77614000000,
         "securitiesAndAssetsJson.longTermJson.nonIslamicPercentage": 0,
         "securitiesAndAssetsJson.longTermJson.retio": 2.6588508863798364,
         "securitiesAndAssetsJson.shortTermJson.amount": 55372,
         "securitiesAndAssetsJson.shortTermJson.amountInOnes": 55372000000,
         "securitiesAndAssetsJson.shortTermJson.islamicAmount": 0,
         "securitiesAndAssetsJson.shortTermJson.islamicAmountInOnes": 0,
         "securitiesAndAssetsJson.shortTermJson.islamicPercentage": 0,
         "securitiesAndAssetsJson.shortTermJson.items": [
            {
                  "amount": "36269",
                  "amountInOnes": 36269000000,
                  "comment": [
                     {
                        "body": "",
                        "files": []
                     }
                  ],
                  "id": "1",
                  "items": [],
                  "itemsNotHalal": 0,
                  "itemsQuestionable": 0,
                  "name": "Cash and cash equivalents",
                  "percentage": 1.2424802586918633,
                  "selector": "NON_ISLAMIC"
            },
            {
                  "amount": "19103",
                  "amountInOnes": 19103000000,
                  "comment": [
                     {
                        "body": "",
                        "files": []
                     }
                  ],
                  "id": "2",
                  "items": [],
                  "itemsNotHalal": 0,
                  "itemsQuestionable": 0,
                  "name": "Marketable securities",
                  "percentage": 0.6544183843444998,
                  "selector": "NON_ISLAMIC"
            }
         ],
         "securitiesAndAssetsJson.shortTermJson.name": "Short-term",
         "securitiesAndAssetsJson.shortTermJson.nonIslamicAmount": 55372,
         "securitiesAndAssetsJson.shortTermJson.nonIslamicAmountInOnes": 55372000000,
         "securitiesAndAssetsJson.shortTermJson.nonIslamicPercentage": 0,
         "securitiesAndAssetsJson.shortTermJson.retio": 1.8968986430363632,
         "securitiesAndAssetsJson.totalAmount": 55372,
         "securitiesAndAssetsJson.totalNonIslamicAmount": 55372,
         "securitiesAndAssetsJson.totalRetio": 4.555749529416199,
         "securitiesAndAssetsRevenue": 132986,
         "securitiesAndAssetsStatus": "Pass",
         "sp_status": "UNCOVERED",
         "status": "COMPLIANT",
         "stockId": "0",
         "stockName": "AAPL",
         "subTickersInfo": [
            {
                  "country": "CO",
                  "country_name": "Colombia",
                  "currency": "COP",
                  "exchange": "BVC",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "13771729749.8782480",
                  "psmcurrency": "COP",
                  "rate": 4032.55241,
                  "shareOutstanding": 14935.83,
                  "subTicker": "AAPL.BC",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "USD",
                  "exchange": "BE",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "psmcurrency": "USD",
                  "rate": 1,
                  "shareOutstanding": 14935.83,
                  "subTicker": "A3LHSQ.BE",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "USD",
                  "exchange": "BE",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "psmcurrency": "USD",
                  "rate": 1,
                  "shareOutstanding": 14935.83,
                  "subTicker": "A18X82.BE",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "USD",
                  "exchange": "BE",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "psmcurrency": "USD",
                  "rate": 1,
                  "shareOutstanding": 14935.83,
                  "subTicker": "A3LHSN.BE",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "USD",
                  "exchange": "BE",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "psmcurrency": "USD",
                  "rate": 1,
                  "shareOutstanding": 14935.83,
                  "subTicker": "A3KLCM.BE",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "USD",
                  "exchange": "BE",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "psmcurrency": "USD",
                  "rate": 1,
                  "shareOutstanding": 14935.83,
                  "subTicker": "A1Z1EU.BE",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "USD",
                  "exchange": "BE",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "psmcurrency": "USD",
                  "rate": 1,
                  "shareOutstanding": 14935.83,
                  "subTicker": "A28W11.BE",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "USD",
                  "exchange": "BE",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "psmcurrency": "USD",
                  "rate": 1,
                  "shareOutstanding": 14935.83,
                  "subTicker": "A2R7JV.BE",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "USD",
                  "exchange": "BE",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "psmcurrency": "USD",
                  "rate": 1,
                  "shareOutstanding": 14935.83,
                  "subTicker": "A281KC.BE",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "USD",
                  "exchange": "BE",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "psmcurrency": "USD",
                  "rate": 1,
                  "shareOutstanding": 14935.83,
                  "subTicker": "A3LHSS.BE",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "USD",
                  "exchange": "BE",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "psmcurrency": "USD",
                  "rate": 1,
                  "shareOutstanding": 14935.83,
                  "subTicker": "A281KA.BE",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "USD",
                  "exchange": "BE",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "psmcurrency": "USD",
                  "rate": 1,
                  "shareOutstanding": 14935.83,
                  "subTicker": "A3KUT6.BE",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "USD",
                  "exchange": "BE",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "psmcurrency": "USD",
                  "rate": 1,
                  "shareOutstanding": 14935.83,
                  "subTicker": "A281KD.BE",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "USD",
                  "exchange": "BE",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "psmcurrency": "USD",
                  "rate": 1,
                  "shareOutstanding": 14935.83,
                  "subTicker": "A3LHSR.BE",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "USD",
                  "exchange": "BE",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "psmcurrency": "USD",
                  "rate": 1,
                  "shareOutstanding": 14935.83,
                  "subTicker": "APC5.BE",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "USD",
                  "exchange": "BE",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "psmcurrency": "USD",
                  "rate": 1,
                  "shareOutstanding": 14935.83,
                  "subTicker": "A3K78V.BE",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "USD",
                  "exchange": "BE",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "psmcurrency": "USD",
                  "rate": 1,
                  "shareOutstanding": 14935.83,
                  "subTicker": "APC1.BE",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "USD",
                  "exchange": "BE",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "psmcurrency": "USD",
                  "rate": 1,
                  "shareOutstanding": 14935.83,
                  "subTicker": "A3K78W.BE",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "USD",
                  "exchange": "BE",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "psmcurrency": "USD",
                  "rate": 1,
                  "shareOutstanding": 14935.83,
                  "subTicker": "A3KLCK.BE",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "USD",
                  "exchange": "BE",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "psmcurrency": "USD",
                  "rate": 1,
                  "shareOutstanding": 14935.83,
                  "subTicker": "A281KB.BE",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "USD",
                  "exchange": "BE",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "psmcurrency": "USD",
                  "rate": 1,
                  "shareOutstanding": 14935.83,
                  "subTicker": "A3LHSP.BE",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "USD",
                  "exchange": "BE",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "psmcurrency": "USD",
                  "rate": 1,
                  "shareOutstanding": 14935.83,
                  "subTicker": "APCC.BE",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "USD",
                  "exchange": "BE",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "psmcurrency": "USD",
                  "rate": 1,
                  "shareOutstanding": 14935.83,
                  "subTicker": "A2R7JW.BE",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "USD",
                  "exchange": "BE",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "psmcurrency": "USD",
                  "rate": 1,
                  "shareOutstanding": 14935.83,
                  "subTicker": "A3KUT5.BE",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "USD",
                  "exchange": "BE",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "psmcurrency": "USD",
                  "rate": 1,
                  "shareOutstanding": 14935.83,
                  "subTicker": "A3KLCL.BE",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "USD",
                  "exchange": "BE",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "psmcurrency": "USD",
                  "rate": 1,
                  "shareOutstanding": 14935.83,
                  "subTicker": "A3KUT3.BE",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "USD",
                  "exchange": "BE",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "psmcurrency": "USD",
                  "rate": 1,
                  "shareOutstanding": 14935.83,
                  "subTicker": "A3KUT4.BE",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "USD",
                  "exchange": "BE",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "psmcurrency": "USD",
                  "rate": 1,
                  "shareOutstanding": 14935.83,
                  "subTicker": "A3K78T.BE",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "EUR",
                  "exchange": "BE",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2922218.4180531",
                  "psmcurrency": "EUR",
                  "rate": 0.858689,
                  "shareOutstanding": 14935.83,
                  "subTicker": "APC.BE",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "USD",
                  "exchange": "BE",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "psmcurrency": "USD",
                  "rate": 1,
                  "shareOutstanding": 14935.83,
                  "subTicker": "A1HKKY.BE",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "USD",
                  "exchange": "BE",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "psmcurrency": "USD",
                  "rate": 1,
                  "shareOutstanding": 14935.83,
                  "subTicker": "A1ZHWH.BE",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "USD",
                  "exchange": "BE",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "psmcurrency": "USD",
                  "rate": 1,
                  "shareOutstanding": 14935.83,
                  "subTicker": "A28W10.BE",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "USD",
                  "exchange": "BE",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "psmcurrency": "USD",
                  "rate": 1,
                  "shareOutstanding": 14935.83,
                  "subTicker": "A3K78U.BE",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "USD",
                  "exchange": "BE",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "psmcurrency": "USD",
                  "rate": 1,
                  "shareOutstanding": 14935.83,
                  "subTicker": "A3KLCH.BE",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "USD",
                  "exchange": "BE",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "psmcurrency": "USD",
                  "rate": 1,
                  "shareOutstanding": 14935.83,
                  "subTicker": "APCU.BE",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "USD",
                  "exchange": "BE",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "psmcurrency": "USD",
                  "rate": 1,
                  "shareOutstanding": 14935.83,
                  "subTicker": "APCX.BE",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "USD",
                  "exchange": "BE",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "psmcurrency": "USD",
                  "rate": 1,
                  "shareOutstanding": 14935.83,
                  "subTicker": "A3KLCG.BE",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "USD",
                  "exchange": "BE",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "psmcurrency": "USD",
                  "rate": 1,
                  "shareOutstanding": 14935.83,
                  "subTicker": "APC4.BE",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "EUR",
                  "exchange": "XETRA",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2922218.4180531",
                  "psmcurrency": "EUR",
                  "rate": 0.858689,
                  "shareOutstanding": 14935.83,
                  "subTicker": "APC.DE",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "USD",
                  "exchange": "XDUS",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "psmcurrency": "USD",
                  "rate": 1,
                  "shareOutstanding": 14935.83,
                  "subTicker": "A1ZVXC.DU",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "USD",
                  "exchange": "XDUS",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "psmcurrency": "USD",
                  "rate": 1,
                  "shareOutstanding": 14935.83,
                  "subTicker": "A18X84.DU",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "USD",
                  "exchange": "XDUS",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "psmcurrency": "USD",
                  "rate": 1,
                  "shareOutstanding": 14935.83,
                  "subTicker": "APCU.DU",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "USD",
                  "exchange": "XDUS",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "psmcurrency": "USD",
                  "rate": 1,
                  "shareOutstanding": 14935.83,
                  "subTicker": "A2R7JW.DU",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "USD",
                  "exchange": "XDUS",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "psmcurrency": "USD",
                  "rate": 1,
                  "shareOutstanding": 14935.83,
                  "subTicker": "APC5.DU",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "USD",
                  "exchange": "XDUS",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "psmcurrency": "USD",
                  "rate": 1,
                  "shareOutstanding": 14935.83,
                  "subTicker": "A1ZHWH.DU",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "USD",
                  "exchange": "XDUS",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "psmcurrency": "USD",
                  "rate": 1,
                  "shareOutstanding": 14935.83,
                  "subTicker": "A281KC.DU",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "USD",
                  "exchange": "XDUS",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "psmcurrency": "USD",
                  "rate": 1,
                  "shareOutstanding": 14935.83,
                  "subTicker": "A19NYJ.DU",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "USD",
                  "exchange": "XDUS",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "psmcurrency": "USD",
                  "rate": 1,
                  "shareOutstanding": 14935.83,
                  "subTicker": "APCX.DU",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "USD",
                  "exchange": "XDUS",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "psmcurrency": "USD",
                  "rate": 1,
                  "shareOutstanding": 14935.83,
                  "subTicker": "APC1.DU",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "USD",
                  "exchange": "XDUS",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "psmcurrency": "USD",
                  "rate": 1,
                  "shareOutstanding": 14935.83,
                  "subTicker": "A3LHSQ.DU",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "USD",
                  "exchange": "XDUS",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "psmcurrency": "USD",
                  "rate": 1,
                  "shareOutstanding": 14935.83,
                  "subTicker": "A281KB.DU",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "USD",
                  "exchange": "XDUS",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "psmcurrency": "USD",
                  "rate": 1,
                  "shareOutstanding": 14935.83,
                  "subTicker": "A3KUT6.DU",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "USD",
                  "exchange": "XDUS",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "psmcurrency": "USD",
                  "rate": 1,
                  "shareOutstanding": 14935.83,
                  "subTicker": "A3LHSP.DU",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "USD",
                  "exchange": "XDUS",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "psmcurrency": "USD",
                  "rate": 1,
                  "shareOutstanding": 14935.83,
                  "subTicker": "A18X82.DU",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "USD",
                  "exchange": "XDUS",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "psmcurrency": "USD",
                  "rate": 1,
                  "shareOutstanding": 14935.83,
                  "subTicker": "A28W11.DU",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "EUR",
                  "exchange": "XDUS",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2625948.8076505",
                  "psmcurrency": "EUR",
                  "rate": 0.858689,
                  "shareOutstanding": 14935.83,
                  "subTicker": "APC8.DU",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "USD",
                  "exchange": "XDUS",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "psmcurrency": "USD",
                  "rate": 1,
                  "shareOutstanding": 14935.83,
                  "subTicker": "A2R7JV.DU",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "USD",
                  "exchange": "XDUS",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "psmcurrency": "USD",
                  "rate": 1,
                  "shareOutstanding": 14935.83,
                  "subTicker": "A3KUT5.DU",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "USD",
                  "exchange": "XDUS",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "psmcurrency": "USD",
                  "rate": 1,
                  "shareOutstanding": 14935.83,
                  "subTicker": "A3KLCG.DU",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "USD",
                  "exchange": "XDUS",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "psmcurrency": "USD",
                  "rate": 1,
                  "shareOutstanding": 14935.83,
                  "subTicker": "A3KLCH.DU",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "USD",
                  "exchange": "XDUS",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "psmcurrency": "USD",
                  "rate": 1,
                  "shareOutstanding": 14935.83,
                  "subTicker": "A3KUT3.DU",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "EUR",
                  "exchange": "XDUS",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2922218.4180531",
                  "psmcurrency": "EUR",
                  "rate": 0.858689,
                  "shareOutstanding": 14935.83,
                  "subTicker": "APC.DU",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "USD",
                  "exchange": "XDUS",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "psmcurrency": "USD",
                  "rate": 1,
                  "shareOutstanding": 14935.83,
                  "subTicker": "A3KLCK.DU",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "USD",
                  "exchange": "XDUS",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "psmcurrency": "USD",
                  "rate": 1,
                  "shareOutstanding": 14935.83,
                  "subTicker": "A28W10.DU",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "USD",
                  "exchange": "XDUS",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "psmcurrency": "USD",
                  "rate": 1,
                  "shareOutstanding": 14935.83,
                  "subTicker": "APC4.DU",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "USD",
                  "exchange": "XDUS",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "psmcurrency": "USD",
                  "rate": 1,
                  "shareOutstanding": 14935.83,
                  "subTicker": "A3KLCM.DU",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "USD",
                  "exchange": "XDUS",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "psmcurrency": "USD",
                  "rate": 1,
                  "shareOutstanding": 14935.83,
                  "subTicker": "A1VQHR.DU",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "USD",
                  "exchange": "XDUS",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "psmcurrency": "USD",
                  "rate": 1,
                  "shareOutstanding": 14935.83,
                  "subTicker": "A281KD.DU",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "USD",
                  "exchange": "XDUS",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "psmcurrency": "USD",
                  "rate": 1,
                  "shareOutstanding": 14935.83,
                  "subTicker": "A3LHSN.DU",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "USD",
                  "exchange": "XDUS",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "psmcurrency": "USD",
                  "rate": 1,
                  "shareOutstanding": 14935.83,
                  "subTicker": "A19HCJ.DU",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "USD",
                  "exchange": "XDUS",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "psmcurrency": "USD",
                  "rate": 1,
                  "shareOutstanding": 14935.83,
                  "subTicker": "APCT.DU",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "USD",
                  "exchange": "XDUS",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "psmcurrency": "USD",
                  "rate": 1,
                  "shareOutstanding": 14935.83,
                  "subTicker": "A18X83.DU",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "USD",
                  "exchange": "XDUS",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "psmcurrency": "USD",
                  "rate": 1,
                  "shareOutstanding": 14935.83,
                  "subTicker": "A1HKKY.DU",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "USD",
                  "exchange": "XDUS",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "psmcurrency": "USD",
                  "rate": 1,
                  "shareOutstanding": 14935.83,
                  "subTicker": "A3KLCL.DU",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "USD",
                  "exchange": "XDUS",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "psmcurrency": "USD",
                  "rate": 1,
                  "shareOutstanding": 14935.83,
                  "subTicker": "A3LHSR.DU",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "USD",
                  "exchange": "XDUS",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "psmcurrency": "USD",
                  "rate": 1,
                  "shareOutstanding": 14935.83,
                  "subTicker": "A3LHSS.DU",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "USD",
                  "exchange": "XDUS",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "psmcurrency": "USD",
                  "rate": 1,
                  "shareOutstanding": 14935.83,
                  "subTicker": "APCC.DU",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "USD",
                  "exchange": "XDUS",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "psmcurrency": "USD",
                  "rate": 1,
                  "shareOutstanding": 14935.83,
                  "subTicker": "A3KUT4.DU",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "USD",
                  "exchange": "XDUS",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "psmcurrency": "USD",
                  "rate": 1,
                  "shareOutstanding": 14935.83,
                  "subTicker": "APC3.DU",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "USD",
                  "exchange": "XDUS",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "psmcurrency": "USD",
                  "rate": 1,
                  "shareOutstanding": 14935.83,
                  "subTicker": "A281KA.DU",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "USD",
                  "exchange": "XDUS",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "psmcurrency": "USD",
                  "rate": 1,
                  "shareOutstanding": 14935.83,
                  "subTicker": "A1Z1EU.DU",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "USD",
                  "exchange": "XDUS",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "psmcurrency": "USD",
                  "rate": 1,
                  "shareOutstanding": 14935.83,
                  "subTicker": "A1VQHT.DU",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "USD",
                  "exchange": "BE",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "psmcurrency": "USD",
                  "rate": 1,
                  "shareOutstanding": 14935.83,
                  "subTicker": "A4EAYM.BE",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "USD",
                  "exchange": "BE",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "psmcurrency": "USD",
                  "rate": 1,
                  "shareOutstanding": 14935.83,
                  "subTicker": "A4EAYK.BE",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "USD",
                  "exchange": "BE",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "psmcurrency": "USD",
                  "rate": 1,
                  "shareOutstanding": 14935.83,
                  "subTicker": "A4EAYL.BE",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "USD",
                  "exchange": "BE",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "psmcurrency": "USD",
                  "rate": 1,
                  "shareOutstanding": 14935.83,
                  "subTicker": "A4EAYJ.BE",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "USD",
                  "exchange": "XDUS",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "psmcurrency": "USD",
                  "rate": 1,
                  "shareOutstanding": 14935.83,
                  "subTicker": "A4EAYK.DU",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "USD",
                  "exchange": "XDUS",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "psmcurrency": "USD",
                  "rate": 1,
                  "shareOutstanding": 14935.83,
                  "subTicker": "A4EAYJ.DU",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "USD",
                  "exchange": "XDUS",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "psmcurrency": "USD",
                  "rate": 1,
                  "shareOutstanding": 14935.83,
                  "subTicker": "A4EAYL.DU",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "USD",
                  "exchange": "XDUS",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "psmcurrency": "USD",
                  "rate": 1,
                  "shareOutstanding": 14935.83,
                  "subTicker": "A4EAYM.DU",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "exchange": "BE",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "rate": 0,
                  "shareOutstanding": 14935.83,
                  "subTicker": "APCJ.BE",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "exchange": "BE",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "rate": 0,
                  "shareOutstanding": 14935.83,
                  "subTicker": "A19R33.BE",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "EUR",
                  "exchange": "XFRA",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2625948.8076505",
                  "psmcurrency": "EUR",
                  "rate": 0.858689,
                  "shareOutstanding": 14935.83,
                  "subTicker": "APC8.F",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "EUR",
                  "exchange": "XFRA",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2922218.4180531",
                  "psmcurrency": "EUR",
                  "rate": 0.858689,
                  "shareOutstanding": 14935.83,
                  "subTicker": "APC.F",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "USD",
                  "exchange": "HAM",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "psmcurrency": "USD",
                  "rate": 1,
                  "shareOutstanding": 14935.83,
                  "subTicker": "APCT.HM",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "USD",
                  "exchange": "HAM",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "psmcurrency": "USD",
                  "rate": 1,
                  "shareOutstanding": 14935.83,
                  "subTicker": "APC3.HM",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "USD",
                  "exchange": "HAM",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "psmcurrency": "USD",
                  "rate": 1,
                  "shareOutstanding": 14935.83,
                  "subTicker": "A1VQHT.HM",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "exchange": "HAM",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "rate": 0,
                  "shareOutstanding": 14935.83,
                  "subTicker": "A19R33.HM",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "USD",
                  "exchange": "MUN",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "psmcurrency": "USD",
                  "rate": 1,
                  "shareOutstanding": 14935.83,
                  "subTicker": "A19HCJ.MU",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "USD",
                  "exchange": "MUN",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "psmcurrency": "USD",
                  "rate": 1,
                  "shareOutstanding": 14935.83,
                  "subTicker": "A3KUT5.MU",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "USD",
                  "exchange": "MUN",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "psmcurrency": "USD",
                  "rate": 1,
                  "shareOutstanding": 14935.83,
                  "subTicker": "A18X82.MU",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "exchange": "MUN",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "rate": 0,
                  "shareOutstanding": 14935.83,
                  "subTicker": "APCJ.MU",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "USD",
                  "exchange": "MUN",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "psmcurrency": "USD",
                  "rate": 1,
                  "shareOutstanding": 14935.83,
                  "subTicker": "A2R7JW.MU",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "USD",
                  "exchange": "MUN",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "psmcurrency": "USD",
                  "rate": 1,
                  "shareOutstanding": 14935.83,
                  "subTicker": "A3KLCK.MU",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "EUR",
                  "exchange": "MUN",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2625948.8076505",
                  "psmcurrency": "EUR",
                  "rate": 0.858689,
                  "shareOutstanding": 14935.83,
                  "subTicker": "APC8.MU",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "USD",
                  "exchange": "MUN",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "psmcurrency": "USD",
                  "rate": 1,
                  "shareOutstanding": 14935.83,
                  "subTicker": "A1HKKY.MU",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "USD",
                  "exchange": "MUN",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "psmcurrency": "USD",
                  "rate": 1,
                  "shareOutstanding": 14935.83,
                  "subTicker": "APCC.MU",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "exchange": "MUN",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "rate": 0,
                  "shareOutstanding": 14935.83,
                  "subTicker": "APCF.MU",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "USD",
                  "exchange": "MUN",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "psmcurrency": "USD",
                  "rate": 1,
                  "shareOutstanding": 14935.83,
                  "subTicker": "A1ZVXC.MU",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "USD",
                  "exchange": "MUN",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "psmcurrency": "USD",
                  "rate": 1,
                  "shareOutstanding": 14935.83,
                  "subTicker": "A3K78U.MU",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "USD",
                  "exchange": "MUN",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "psmcurrency": "USD",
                  "rate": 1,
                  "shareOutstanding": 14935.83,
                  "subTicker": "A3KLCH.MU",
                  "tickerIsMain": false
            },
            {
                  "country": "CL",
                  "country_name": "Chile",
                  "currency": "CLP",
                  "exchange": "SSE",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "3295679985.7255864",
                  "psmcurrency": "CLP",
                  "rate": 968.67,
                  "shareOutstanding": 14935.83,
                  "subTicker": "AAPLCL.SN",
                  "tickerIsMain": false
            },
            {
                  "country": "GB",
                  "country_name": "UK",
                  "currency": "",
                  "exchange": "TRQX",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "3403643.4564480",
                  "psmcurrency": "",
                  "rate": 0,
                  "shareOutstanding": 14935.83,
                  "subTicker": "AAPLN.TU",
                  "tickerIsMain": false
            },
            {
                  "country": "US",
                  "country_name": "US",
                  "currency": "USD",
                  "exchange": "NASDAQ",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "3403643.3469861",
                  "psmcurrency": "USD",
                  "rate": 1,
                  "shareOutstanding": 14935.83,
                  "subTicker": "AAPL",
                  "tickerIsMain": true
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "USD",
                  "exchange": "HAN",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "psmcurrency": "USD",
                  "rate": 1,
                  "shareOutstanding": 14935.83,
                  "subTicker": "A3KLCH.HA",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "USD",
                  "exchange": "HAN",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "psmcurrency": "USD",
                  "rate": 1,
                  "shareOutstanding": 14935.83,
                  "subTicker": "A19NYJ.HA",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "exchange": "HAN",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "rate": 0,
                  "shareOutstanding": 14935.83,
                  "subTicker": "APCF.HA",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "USD",
                  "exchange": "HAN",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "psmcurrency": "USD",
                  "rate": 1,
                  "shareOutstanding": 14935.83,
                  "subTicker": "A1HKKY.HA",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "USD",
                  "exchange": "HAN",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "psmcurrency": "USD",
                  "rate": 1,
                  "shareOutstanding": 14935.83,
                  "subTicker": "APC4.HA",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "USD",
                  "exchange": "HAN",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "psmcurrency": "USD",
                  "rate": 1,
                  "shareOutstanding": 14935.83,
                  "subTicker": "A1VQHT.HA",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "USD",
                  "exchange": "MUN",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "psmcurrency": "USD",
                  "rate": 1,
                  "shareOutstanding": 14935.83,
                  "subTicker": "A4EAYL.MU",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "exchange": "BE",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "rate": 0,
                  "shareOutstanding": 14935.83,
                  "subTicker": "A19NYJ.BE",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "exchange": "XDUS",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "rate": 0,
                  "shareOutstanding": 14935.83,
                  "subTicker": "APCF.DU",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "exchange": "XDUS",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "rate": 0,
                  "shareOutstanding": 14935.83,
                  "subTicker": "A28W1Z.DU",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "exchange": "XDUS",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "rate": 0,
                  "shareOutstanding": 14935.83,
                  "subTicker": "A19R33.DU",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "USD",
                  "exchange": "HAM",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "psmcurrency": "USD",
                  "rate": 1,
                  "shareOutstanding": 14935.83,
                  "subTicker": "A1HKKY.HM",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "USD",
                  "exchange": "HAM",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "psmcurrency": "USD",
                  "rate": 1,
                  "shareOutstanding": 14935.83,
                  "subTicker": "A19HCJ.HM",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "USD",
                  "exchange": "HAM",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "psmcurrency": "USD",
                  "rate": 1,
                  "shareOutstanding": 14935.83,
                  "subTicker": "A18X84.HM",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "USD",
                  "exchange": "HAM",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "psmcurrency": "USD",
                  "rate": 1,
                  "shareOutstanding": 14935.83,
                  "subTicker": "APC1.HM",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "USD",
                  "exchange": "HAM",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "psmcurrency": "USD",
                  "rate": 1,
                  "shareOutstanding": 14935.83,
                  "subTicker": "APC4.HM",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "USD",
                  "exchange": "MUN",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "psmcurrency": "USD",
                  "rate": 1,
                  "shareOutstanding": 14935.83,
                  "subTicker": "A1VQHR.MU",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "USD",
                  "exchange": "MUN",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "psmcurrency": "USD",
                  "rate": 1,
                  "shareOutstanding": 14935.83,
                  "subTicker": "A3KUT3.MU",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "USD",
                  "exchange": "MUN",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "psmcurrency": "USD",
                  "rate": 1,
                  "shareOutstanding": 14935.83,
                  "subTicker": "APC4.MU",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "USD",
                  "exchange": "MUN",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "psmcurrency": "USD",
                  "rate": 1,
                  "shareOutstanding": 14935.83,
                  "subTicker": "A3KLCM.MU",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "exchange": "MUN",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "rate": 0,
                  "shareOutstanding": 14935.83,
                  "subTicker": "A28W1Z.MU",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "USD",
                  "exchange": "MUN",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "psmcurrency": "USD",
                  "rate": 1,
                  "shareOutstanding": 14935.83,
                  "subTicker": "A3LHSP.MU",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "USD",
                  "exchange": "MUN",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "psmcurrency": "USD",
                  "rate": 1,
                  "shareOutstanding": 14935.83,
                  "subTicker": "A281KB.MU",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "USD",
                  "exchange": "MUN",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "psmcurrency": "USD",
                  "rate": 1,
                  "shareOutstanding": 14935.83,
                  "subTicker": "A281KC.MU",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "USD",
                  "exchange": "MUN",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "psmcurrency": "USD",
                  "rate": 1,
                  "shareOutstanding": 14935.83,
                  "subTicker": "A28W11.MU",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "USD",
                  "exchange": "MUN",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "psmcurrency": "USD",
                  "rate": 1,
                  "shareOutstanding": 14935.83,
                  "subTicker": "A3LHSR.MU",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "USD",
                  "exchange": "MUN",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "psmcurrency": "USD",
                  "rate": 1,
                  "shareOutstanding": 14935.83,
                  "subTicker": "A28W10.MU",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "USD",
                  "exchange": "MUN",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "psmcurrency": "USD",
                  "rate": 1,
                  "shareOutstanding": 14935.83,
                  "subTicker": "A2R7JV.MU",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "EUR",
                  "exchange": "MUN",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2922218.4180531",
                  "psmcurrency": "EUR",
                  "rate": 0.858689,
                  "shareOutstanding": 14935.83,
                  "subTicker": "APC.MU",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "USD",
                  "exchange": "MUN",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "psmcurrency": "USD",
                  "rate": 1,
                  "shareOutstanding": 14935.83,
                  "subTicker": "A3KLCL.MU",
                  "tickerIsMain": false
            },
            {
                  "country": "CA",
                  "country_name": "Canada",
                  "currency": "CAD",
                  "exchange": "NEO",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "4124420.1818753",
                  "psmcurrency": "CAD",
                  "rate": 1.378236,
                  "shareOutstanding": 14935.83,
                  "subTicker": "AAPL.NE",
                  "tickerIsMain": false
            },
            {
                  "country": "CH",
                  "country_name": "Switzerland",
                  "currency": "EUR",
                  "exchange": "SWX",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2922218.4180531",
                  "psmcurrency": "EUR",
                  "rate": 0.858689,
                  "shareOutstanding": 14935.83,
                  "subTicker": "AAPLEUR.SW",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "USD",
                  "exchange": "HAN",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "psmcurrency": "USD",
                  "rate": 1,
                  "shareOutstanding": 14935.83,
                  "subTicker": "A1ZVXC.HA",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "USD",
                  "exchange": "HAN",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "psmcurrency": "USD",
                  "rate": 1,
                  "shareOutstanding": 14935.83,
                  "subTicker": "A19HCJ.HA",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "exchange": "HAN",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "rate": 0,
                  "shareOutstanding": 14935.83,
                  "subTicker": "A19R33.HA",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "USD",
                  "exchange": "MUN",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "psmcurrency": "USD",
                  "rate": 1,
                  "shareOutstanding": 14935.83,
                  "subTicker": "A4EAYK.MU",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "exchange": "BE",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "rate": 0,
                  "shareOutstanding": 14935.83,
                  "subTicker": "A2R7JT.BE",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "exchange": "XDUS",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "rate": 0,
                  "shareOutstanding": 14935.83,
                  "subTicker": "A2R7JT.DU",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "USD",
                  "exchange": "HAM",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "psmcurrency": "USD",
                  "rate": 1,
                  "shareOutstanding": 14935.83,
                  "subTicker": "A1VQHR.HM",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "USD",
                  "exchange": "HAM",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "psmcurrency": "USD",
                  "rate": 1,
                  "shareOutstanding": 14935.83,
                  "subTicker": "A19NYJ.HM",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "USD",
                  "exchange": "HAM",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "psmcurrency": "USD",
                  "rate": 1,
                  "shareOutstanding": 14935.83,
                  "subTicker": "APCU.HM",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "exchange": "HAM",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "rate": 0,
                  "shareOutstanding": 14935.83,
                  "subTicker": "APCJ.HM",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "USD",
                  "exchange": "MUN",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "psmcurrency": "USD",
                  "rate": 1,
                  "shareOutstanding": 14935.83,
                  "subTicker": "A3LHSS.MU",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "USD",
                  "exchange": "MUN",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "psmcurrency": "USD",
                  "rate": 1,
                  "shareOutstanding": 14935.83,
                  "subTicker": "APC3.MU",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "USD",
                  "exchange": "MUN",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "psmcurrency": "USD",
                  "rate": 1,
                  "shareOutstanding": 14935.83,
                  "subTicker": "A3LHSQ.MU",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "exchange": "MUN",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "rate": 0,
                  "shareOutstanding": 14935.83,
                  "subTicker": "A2R7JT.MU",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "USD",
                  "exchange": "MUN",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "psmcurrency": "USD",
                  "rate": 1,
                  "shareOutstanding": 14935.83,
                  "subTicker": "A1Z1EU.MU",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "USD",
                  "exchange": "MUN",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "psmcurrency": "USD",
                  "rate": 1,
                  "shareOutstanding": 14935.83,
                  "subTicker": "A3KLCG.MU",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "USD",
                  "exchange": "MUN",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "psmcurrency": "USD",
                  "rate": 1,
                  "shareOutstanding": 14935.83,
                  "subTicker": "APCX.MU",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "USD",
                  "exchange": "MUN",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "psmcurrency": "USD",
                  "rate": 1,
                  "shareOutstanding": 14935.83,
                  "subTicker": "A19NYJ.MU",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "USD",
                  "exchange": "MUN",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "psmcurrency": "USD",
                  "rate": 1,
                  "shareOutstanding": 14935.83,
                  "subTicker": "APCT.MU",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "USD",
                  "exchange": "MUN",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "psmcurrency": "USD",
                  "rate": 1,
                  "shareOutstanding": 14935.83,
                  "subTicker": "A3K78W.MU",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "USD",
                  "exchange": "MUN",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "psmcurrency": "USD",
                  "rate": 1,
                  "shareOutstanding": 14935.83,
                  "subTicker": "A18X83.MU",
                  "tickerIsMain": false
            },
            {
                  "country": "MX",
                  "country_name": "Mexico",
                  "currency": "MXN",
                  "exchange": "BMV",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "63264202.2145934",
                  "psmcurrency": "MXN",
                  "rate": 18.649149,
                  "shareOutstanding": 14935.83,
                  "subTicker": "AAPL.MX",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "USD",
                  "exchange": "HAN",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "psmcurrency": "USD",
                  "rate": 1,
                  "shareOutstanding": 14935.83,
                  "subTicker": "APC1.HA",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "USD",
                  "exchange": "HAN",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "psmcurrency": "USD",
                  "rate": 1,
                  "shareOutstanding": 14935.83,
                  "subTicker": "APCT.HA",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "USD",
                  "exchange": "HAN",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "psmcurrency": "USD",
                  "rate": 1,
                  "shareOutstanding": 14935.83,
                  "subTicker": "APCU.HA",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "USD",
                  "exchange": "HAN",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "psmcurrency": "USD",
                  "rate": 1,
                  "shareOutstanding": 14935.83,
                  "subTicker": "A1VQHR.HA",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "USD",
                  "exchange": "MUN",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "psmcurrency": "USD",
                  "rate": 1,
                  "shareOutstanding": 14935.83,
                  "subTicker": "A4EAYM.MU",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "exchange": "BE",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "rate": 0,
                  "shareOutstanding": 14935.83,
                  "subTicker": "A28W1Z.BE",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "exchange": "XDUS",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "rate": 0,
                  "shareOutstanding": 14935.83,
                  "subTicker": "APCJ.DU",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "USD",
                  "exchange": "HAM",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "psmcurrency": "USD",
                  "rate": 1,
                  "shareOutstanding": 14935.83,
                  "subTicker": "A1ZVXC.HM",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "exchange": "HAM",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "rate": 0,
                  "shareOutstanding": 14935.83,
                  "subTicker": "APCF.HM",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "EUR",
                  "exchange": "HAM",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2922218.4180531",
                  "psmcurrency": "EUR",
                  "rate": 0.858689,
                  "shareOutstanding": 14935.83,
                  "subTicker": "APC.HM",
                  "tickerIsMain": false
            },
            {
                  "country": "GB",
                  "country_name": "UK",
                  "currency": "USD",
                  "exchange": "LSE",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "3403643.4564480",
                  "psmcurrency": "USD",
                  "rate": 1,
                  "shareOutstanding": 14935.83,
                  "subTicker": "0R2V.L",
                  "tickerIsMain": false
            },
            {
                  "country": "IT",
                  "country_name": "Italy",
                  "currency": "EUR",
                  "exchange": "MIL",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2922218.4180531",
                  "psmcurrency": "EUR",
                  "rate": 0.858689,
                  "shareOutstanding": 14935.83,
                  "subTicker": "1AAPL.MI",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "USD",
                  "exchange": "MUN",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "psmcurrency": "USD",
                  "rate": 1,
                  "shareOutstanding": 14935.83,
                  "subTicker": "APCU.MU",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "USD",
                  "exchange": "MUN",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "psmcurrency": "USD",
                  "rate": 1,
                  "shareOutstanding": 14935.83,
                  "subTicker": "A3K78T.MU",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "USD",
                  "exchange": "MUN",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "psmcurrency": "USD",
                  "rate": 1,
                  "shareOutstanding": 14935.83,
                  "subTicker": "APC5.MU",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "USD",
                  "exchange": "MUN",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "psmcurrency": "USD",
                  "rate": 1,
                  "shareOutstanding": 14935.83,
                  "subTicker": "A18X84.MU",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "USD",
                  "exchange": "MUN",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "psmcurrency": "USD",
                  "rate": 1,
                  "shareOutstanding": 14935.83,
                  "subTicker": "A281KA.MU",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "USD",
                  "exchange": "MUN",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "psmcurrency": "USD",
                  "rate": 1,
                  "shareOutstanding": 14935.83,
                  "subTicker": "A3KUT6.MU",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "USD",
                  "exchange": "MUN",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "psmcurrency": "USD",
                  "rate": 1,
                  "shareOutstanding": 14935.83,
                  "subTicker": "A1VQHT.MU",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "USD",
                  "exchange": "MUN",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "psmcurrency": "USD",
                  "rate": 1,
                  "shareOutstanding": 14935.83,
                  "subTicker": "A3KUT4.MU",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "exchange": "MUN",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "rate": 0,
                  "shareOutstanding": 14935.83,
                  "subTicker": "A19R33.MU",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "USD",
                  "exchange": "MUN",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "psmcurrency": "USD",
                  "rate": 1,
                  "shareOutstanding": 14935.83,
                  "subTicker": "A3K78V.MU",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "USD",
                  "exchange": "MUN",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "psmcurrency": "USD",
                  "rate": 1,
                  "shareOutstanding": 14935.83,
                  "subTicker": "A281KD.MU",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "USD",
                  "exchange": "MUN",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "psmcurrency": "USD",
                  "rate": 1,
                  "shareOutstanding": 14935.83,
                  "subTicker": "A3LHSN.MU",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "USD",
                  "exchange": "MUN",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "psmcurrency": "USD",
                  "rate": 1,
                  "shareOutstanding": 14935.83,
                  "subTicker": "APC1.MU",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "USD",
                  "exchange": "MUN",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "psmcurrency": "USD",
                  "rate": 1,
                  "shareOutstanding": 14935.83,
                  "subTicker": "A1ZHWH.MU",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "EUR",
                  "exchange": "XSTU",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2922218.4180531",
                  "psmcurrency": "EUR",
                  "rate": 0.858689,
                  "shareOutstanding": 14935.83,
                  "subTicker": "APC.SG",
                  "tickerIsMain": false
            },
            {
                  "country": "CL",
                  "country_name": "Chile",
                  "currency": "USD",
                  "exchange": "SSE",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "3403643.4564480",
                  "psmcurrency": "USD",
                  "rate": 1,
                  "shareOutstanding": 14935.83,
                  "subTicker": "AAPL.SN",
                  "tickerIsMain": false
            },
            {
                  "country": "CH",
                  "country_name": "Switzerland",
                  "currency": "USD",
                  "exchange": "SWX",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "3403643.4564480",
                  "psmcurrency": "USD",
                  "rate": 1,
                  "shareOutstanding": 14935.83,
                  "subTicker": "AAPLUSD.SW",
                  "tickerIsMain": false
            },
            {
                  "country": "CH",
                  "country_name": "Switzerland",
                  "currency": "CHF",
                  "exchange": "SWX",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2748918.6558927",
                  "psmcurrency": "CHF",
                  "rate": 0.801731,
                  "shareOutstanding": 14935.83,
                  "subTicker": "AAPL.SW",
                  "tickerIsMain": false
            },
            {
                  "country": "AT",
                  "country_name": "Austria",
                  "currency": "EUR",
                  "exchange": "WBAG",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2922218.4180531",
                  "psmcurrency": "EUR",
                  "rate": 0.858689,
                  "shareOutstanding": 14935.83,
                  "subTicker": "AAPL.VI",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "EUR",
                  "exchange": "HAN",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2922218.4180531",
                  "psmcurrency": "EUR",
                  "rate": 0.858689,
                  "shareOutstanding": 14935.83,
                  "subTicker": "APC.HA",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "USD",
                  "exchange": "HAN",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "psmcurrency": "USD",
                  "rate": 1,
                  "shareOutstanding": 14935.83,
                  "subTicker": "APC3.HA",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "USD",
                  "exchange": "HAN",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "psmcurrency": "USD",
                  "rate": 1,
                  "shareOutstanding": 14935.83,
                  "subTicker": "A18X84.HA",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "exchange": "HAN",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "rate": 0,
                  "shareOutstanding": 14935.83,
                  "subTicker": "APCJ.HA",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "EUR",
                  "exchange": "XGAT",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2922218.4180531",
                  "psmcurrency": "EUR",
                  "rate": 0.858689,
                  "shareOutstanding": 14935.83,
                  "subTicker": "APC.TG",
                  "tickerIsMain": false
            },
            {
                  "country": "PL",
                  "country_name": "Poland",
                  "currency": "PLN",
                  "exchange": "GPW",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "12404592.4308134",
                  "psmcurrency": "PLN",
                  "rate": 3.662945,
                  "shareOutstanding": 14935.83,
                  "subTicker": "AAPL.WA",
                  "tickerIsMain": false
            },
            {
                  "country": "DE",
                  "country_name": "Germany",
                  "currency": "USD",
                  "exchange": "MUN",
                  "mainTicker": "AAPL",
                  "marketCapitalization": "2986418.4157824",
                  "psmcurrency": "USD",
                  "rate": 1,
                  "shareOutstanding": 14935.83,
                  "subTicker": "A4EAYJ.MU",
                  "tickerIsMain": false
            }
         ],
         "totalAssets": 331495,
         "totalRevenue": 316569490000,
         "trailing36MonAvrCap": 2919080584683.54,
         "units": 1000000,
         "usdRate": 1,
         "userId": "84",
         "username": "azizbek.kholmurod@musaffa.com"
      }
      

      return of(mockData);
   }

   getEtfDetailedComplianceReport(ticker: string): Observable<any>{
      return this.http.post<any>(this.newApi + `/api/user/get-etf-detailed-compliance-report`, { ticker })
   }

}
